import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button } from 'react-bootstrap';
import { Mail, Plus } from 'lucide-react';
import { standaloneProjectService } from './standaloneProjectService';
import type { StandaloneLayeredMedia } from '@/modules/kinema/layeredMediaService';
import { ProjectCard } from './ProjectCard';
import { NewProjectModal } from '@/modules/kinema/shell/NewProjectModal';
import { Editor, pathPointsInterpolator, UPDATE_PROPERTY } from '@/lib/kinema';
import { appStore } from '@store/appStore';
import { Paths } from '@/paths';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';

export function ProjectListPage() {
  useDocumentTitle('Create Studio');
  const navigate = useNavigate();
  const [projects, setProjects] = useState<StandaloneLayeredMedia[]>([]);
  const [loading, setLoading] = useState(true);
  /**
   * Which kind of canvas the New dialog is offering, or null when it is closed.
   *
   * A postcard starts on a postcard: the screen presets are 16:9 and a card designed on one
   * arrives with the wrong proportions, upscaled, at the printer.
   */
  const [showNew, setShowNew] = useState<'screen' | 'direct_mail' | null>(null);

  useEffect(() => {
    const orgId = appStore.loggedInUser.organization.id;
    standaloneProjectService
      .list(orgId)
      .then((rows) => setProjects(rows))
      .finally(() => setLoading(false));
  }, []);

  const handleRename = async (id: number, name: string) => {
    const prev = projects;
    setProjects(prev.map((p) => (p.id === id ? { ...p, name } : p)));
    try {
      await standaloneProjectService.rename(id, name);
    } catch {
      setProjects(prev);
    }
  };

  const handleDelete = async (id: number) => {
    const prev = projects;
    setProjects(prev.filter((p) => p.id !== id));
    try {
      await standaloneProjectService.delete(id);
    } catch {
      setProjects(prev);
    }
  };

  const handleDuplicate = async (id: number) => {
    const copy = await standaloneProjectService.duplicate(id);
    setProjects([copy, ...projects]);
  };

  const handleCreate = async (cfg: {
    name: string;
    width: number;
    height: number;
    duration: number;
    frameRate: number;
  }) => {
    const editor = new Editor({
      canvas: { width: cfg.width, height: cfg.height },
      frameRate: cfg.frameRate,
      middleware: [],
      devtools: false,
    });
    editor.registerInterpolator('pathPoints', pathPointsInterpolator);
    editor.setDuration(cfg.duration);
    editor.dispatchCommand(UPDATE_PROPERTY, {
      layerId: editor.getState().rootLayerId,
      property: 'name',
      value: cfg.name,
    });
    const result = await standaloneProjectService.create({
      name: cfg.name,
      organization_id: appStore.loggedInUser.organization.id,
      data: editor.toJSON(),
    });
    navigate(`/${Paths.CREATE}/projects/${result.id}`);
  };

  const newProjectBtn = (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => setShowNew('direct_mail')}
        className="d-flex align-items-center gap-1"
      >
        <Mail size={14} /> New Postcard
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={() => setShowNew('screen')}
        className="d-flex align-items-center gap-1"
      >
        <Plus size={14} /> New Project
      </Button>
    </div>
  );

  return (
    <div className="p-3 h-100">
      <Card className="custom-card h-100 d-flex flex-column">
        <Card.Header className="card-header flex-shrink-0 d-flex align-items-center justify-content-between">
          <Card.Title className="mb-0">Create</Card.Title>
          {newProjectBtn}
        </Card.Header>
        <Card.Body className="flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>Loading…</div>
          ) : projects.length === 0 ? (
            <div style={{ padding: 80, textAlign: 'center', color: '#64748b' }}>
              <p style={{ fontSize: 16, marginBottom: 12 }}>Start your first project</p>
              <div className="d-flex gap-2 justify-content-center">
                <Button variant="outline-primary" onClick={() => setShowNew('direct_mail')}>
                  + New Postcard
                </Button>
                <Button variant="primary" onClick={() => setShowNew('screen')}>
                  + New Project
                </Button>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 16,
              }}
            >
              {projects.map((p) => (
                <ProjectCard
                  key={p.id}
                  project={p as any}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                />
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {showNew && (
        <NewProjectModal
          purpose={showNew}
          onCreated={async (cfg) => {
            setShowNew(null);
            await handleCreate(cfg);
          }}
        />
      )}
    </div>
  );
}
